import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import authFetch from '../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('sl-SI');
};

const getCategoryColor = (category) => {
  const normalized = String(category || '').toLowerCase();
  if (normalized === 'admin') return { background: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' };
  if (normalized === 'selling') return { background: '#fef3c7', text: '#b45309', border: '#fcd34d' };
  if (normalized === 'lead-gen') return { background: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' };
  if (normalized === 'contacts') return { background: '#ccfbf1', text: '#0f766e', border: '#5eead4' };
  if (normalized === 'cold-calling') return { background: '#fee2e2', text: '#b91c1c', border: '#fca5a5' };
  return { background: '#e2e8f0', text: '#334155', border: '#cbd5e1' };
};

const buildPayloadPreview = (entry) => {
  const payload = entry.payload || {};

  if (Array.isArray(payload.changes) && payload.changes.length > 0) {
    if (payload.changes.length === 1) {
      const change = payload.changes[0];
      return `${change.fieldName}: ${String(change.oldValue || '')} -> ${String(change.newValue || '')}`;
    }

    const changedFields = payload.changes
      .map((change) => change.fieldName)
      .filter(Boolean)
      .join(', ');

    return changedFields ? `Fields: ${changedFields}` : `Changed ${payload.changes.length} fields`;
  }

  if (payload.label && payload.fieldName && payload.newValue !== undefined) {
    return `${payload.label}: ${payload.fieldName} -> ${String(payload.newValue)}`;
  }

  if (payload.noteContent) {
    return payload.noteContent;
  }

  if (payload.files && Array.isArray(payload.files) && payload.files.length > 0) {
    return payload.files.join(', ');
  }

  if (payload.documentName) {
    return payload.documentName;
  }

  return null;
};

const getEntryDestination = (entry) => {
  if (!entry?.entityId) return null;

  if (entry.category === 'lead-gen' && entry.entityType === 'property_lead') {
    return `/real-estate/lead-gen?leadId=${entry.entityId}`;
  }

  if (entry.category === 'selling' && entry.entityType === 'selling_property') {
    return `/selling/${entry.entityId}`;
  }

  if (entry.category === 'contacts' && entry.entityType === 'contact') {
    return `/real-estate/contacts?contactId=${entry.entityId}`;
  }

  if (entry.category === 'cold-calling' && entry.entityType === 'cold_calling_lead') {
    return `/real-estate/lead-gen?coldCallingLeadId=${entry.entityId}`;
  }

  return null;
};

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ createdByOptions: [], categoryOptions: [] });
  const [dateFrom, setDateFrom] = useState('');
  const [selectedCreatedBy, setSelectedCreatedBy] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadEntries = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      setError('');
      const params = new URLSearchParams({ limit: '300' });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (selectedCreatedBy) params.set('createdBy', selectedCreatedBy);
      if (selectedCategory) params.set('category', selectedCategory);

      const response = await authFetch(`${API_BASE}/api/my-time/activity-log?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load activity log');
      }

      const data = await response.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setFilterOptions({
        createdByOptions: Array.isArray(data.filters?.createdByOptions) ? data.filters.createdByOptions : [],
        categoryOptions: Array.isArray(data.filters?.categoryOptions) ? data.filters.categoryOptions : []
      });
    } catch (err) {
      console.error('Error loading activity log:', err);
      setError(err.message || 'Failed to load activity log');
      setEntries([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, [dateFrom, selectedCreatedBy, selectedCategory]);

  const groupedEntries = useMemo(() => entries, [entries]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <button
            onClick={() => navigate('/my-time')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0, marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}
          >
            <ArrowLeft size={16} /> Back to My Time
          </button>
          <h1 style={{ color: '#1e293b', margin: 0 }}>Activity Log</h1>
          <p style={{ color: '#64748b', marginTop: '8px', marginBottom: 0 }}>A combined timeline of app changes, updates, and system actions.</p>
        </div>

        <button
          onClick={() => loadEntries(true)}
          disabled={loading || refreshing}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#334155', cursor: loading || refreshing ? 'not-allowed' : 'pointer', fontWeight: '600' }}
        >
          <RefreshCcw size={16} /> {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        <div>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Who did it</label>
          <select
            value={selectedCreatedBy}
            onChange={(event) => setSelectedCreatedBy(event.target.value)}
            style={inputStyle}
          >
            <option value="">All users</option>
            {filterOptions.createdByOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Part of the app</label>
          <select
            value={selectedCategory}
            onChange={(event) => setSelectedCategory(event.target.value)}
            style={inputStyle}
          >
            <option value="">All parts</option>
            {filterOptions.categoryOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => {
              setDateFrom('');
              setSelectedCreatedBy('');
              setSelectedCategory('');
            }}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#334155', cursor: 'pointer', fontWeight: '600' }}
          >
            Clear filters
          </button>
        </div>
      </div>

      {loading ? (
        <div style={emptyStateStyle}>Loading activity log...</div>
      ) : error ? (
        <div style={{ ...emptyStateStyle, color: '#b91c1c', border: '1px solid #fecaca', backgroundColor: '#fef2f2' }}>{error}</div>
      ) : groupedEntries.length === 0 ? (
        <div style={emptyStateStyle}>No activity found yet.</div>
      ) : (
        <div style={{ display: 'grid', gap: '14px' }}>
          {groupedEntries.map((entry) => {
            const badge = getCategoryColor(entry.category);
            const payloadPreview = buildPayloadPreview(entry);
            const destination = getEntryDestination(entry);
            const isClickable = Boolean(destination);

            return (
              <div
                key={entry.id}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? () => navigate(destination) : undefined}
                onKeyDown={isClickable ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(destination);
                  }
                } : undefined}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '18px 20px',
                  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)',
                  cursor: isClickable ? 'pointer' : 'default'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '260px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '999px', border: `1px solid ${badge.border}`, backgroundColor: badge.background, color: badge.text, fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {entry.category || 'activity'}
                      </span>
                      {entry.eventType && (
                        <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>{entry.eventType}</span>
                      )}
                    </div>

                    <div style={{ color: '#0f172a', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>{entry.title}</div>
                    {entry.description && (
                      <div style={{ color: '#334155', fontSize: '14px', lineHeight: '1.6', marginBottom: payloadPreview ? '8px' : 0 }}>{entry.description}</div>
                    )}
                    {payloadPreview && (
                      <div style={{ color: '#475569', fontSize: '13px', lineHeight: '1.6' }}>{payloadPreview}</div>
                    )}
                  </div>

                  <div style={{ minWidth: '200px', color: '#64748b', fontSize: '13px', textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>{formatDateTime(entry.createdAt)}</div>
                    <div>By: {entry.createdBy || 'System'}</div>
                    {entry.entityType && (
                      <div style={{ marginTop: '4px' }}>{entry.entityType}{entry.entityId ? ` #${entry.entityId}` : ''}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const emptyStateStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '14px',
  padding: '24px',
  color: '#64748b'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#475569',
  fontWeight: '600',
  fontSize: '13px'
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  borderRadius: '10px',
  border: '1px solid #cbd5e1',
  backgroundColor: '#ffffff',
  color: '#1e293b',
  fontSize: '14px',
  boxSizing: 'border-box'
};

export default ActivityLogPage;